/**
 * « Ce qui a été vu » : le parent déclare ce que la classe a traité (§6.11).
 *
 * Hedi, le 8 septembre 2026 : *« Je veux aussi un nouveau sous tab pour chacun
 * des enfants où je peux 1. sélectionner des compétences par matières pour les
 * considérer comme vues. 2. chatter avec le tuteur pour savoir déterminer
 * quelles compétences peuvent être considérées vues. »*
 *
 * **Pourquoi cet écran existe.** La frontière du programme additionne trois
 * sources ; la troisième — le découpage théorique de l'année — n'a été signée
 * par aucun professeur. Le même jour, le débordement du plan du soir sur les
 * jours suivants a été borné aux compétences qui portent une *preuve*, ce qui
 * ferme le mauvais côté et laisse l'autre ouvert : personne ne pouvait apporter
 * la preuve manquante. C'est ce que cet écran donne, et lui seul.
 *
 * **Trois faits par ligne, jamais un état unique.** « Le cahier de textes le
 * dit », « je l'ai coché » et « la période l'ouvre » appellent trois gestes
 * différents ; les résumer en un mot rendrait l'écran inutile, puisque c'est
 * exactement la distinction que le parent vient faire.
 *
 * **Ce que cocher emporte, et l'écran le dit avant le clic.** Une déclaration
 * vaut preuve partout : elle ouvre le chapitre au travail du soir *et* elle
 * lève le « pas encore vu » que l'enfant aurait posé dessus. Un parent qui
 * coche décide contre son enfant, et il doit le savoir.
 *
 * **Le tuteur ne coche rien.** Il propose, le parent valide case par case. Un
 * modèle qui écrirait directement dans la frontière du programme mettrait une
 * phrase interprétée là où il faut un geste.
 *
 * **Deux rangements, un seul écran** (27 septembre 2026, `VuEnClasseLayout`) :
 * la liste dans l'ordre du référentiel, avec le tuteur, et la vue par chapitre
 * — chapitres des ouvrages quand un livre couvre la compétence, thèmes du
 * programme sinon. Le rangement vient du serveur (`chapitres`), et chaque
 * compétence y figure une fois : une case en double ferait déclarer deux fois
 * la même chose.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  ChapitreDuProgrammeOut,
  EtatDeCompetenceOut,
  EtatDuProgramme,
  MatiereDuProgrammeOut,
  PropositionDeVu,
} from "@/api/schema";
import { useEnfants } from "@/ui/enfants";
import { enClair } from "@/ui/dates";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Tour = { role: "parent" | "tuteur"; texte: string };

/** Ce que la ligne dit d'elle-même, dans l'ordre où le parent doit le lire. */
function etatDe(competence: EtatDeCompetenceOut): { mot: string; detail: string } {
  if (competence.preuve) return { mot: "Vu", detail: competence.preuve };
  if (competence.declaree_le) {
    return { mot: "Déclaré", detail: `vous l'avez coché pour le ${enClair(competence.declaree_le)}` };
  }
  if (competence.ouverte) {
    return {
      mot: "Supposé",
      detail: "ouvert par le calendrier de l'année, sans qu'aucune source ne le confirme",
    };
  }
  return { mot: "Pas encore", detail: "ni vu par une source, ni ouvert par le calendrier" };
}

/** D'où vient le titre du chapitre : un parent qui cherche « Oral » dans le
 *  cahier de son enfant ne le trouvera pas, et doit savoir pourquoi. */
function origineDe(chapitre: ChapitreDuProgrammeOut): string {
  if (!chapitre.ouvrage) return "Thème du programme";
  return chapitre.page ? `${chapitre.ouvrage}, p. ${chapitre.page}` : chapitre.ouvrage;
}

function LigneDeCompetence({
  competence,
  envoi,
  onBascule,
}: {
  competence: EtatDeCompetenceOut;
  envoi: string | null;
  onBascule: (competence: EtatDeCompetenceOut, vu: boolean) => void;
}) {
  const dit = etatDe(competence);
  const prouvee = Boolean(competence.preuve);
  return (
    <label className="check">
      <input
        type="checkbox"
        checked={prouvee || Boolean(competence.declaree_le)}
        disabled={prouvee || envoi === competence.competency_id}
        onChange={(evenement) => onBascule(competence, evenement.target.checked)}
      />
      <span>
        {competence.label}
        <br />
        <span className="muted">
          {dit.mot} — {dit.detail}
        </span>
      </span>
    </label>
  );
}

export function VuEnClasseScreen({ vue = "liste" }: { vue?: "liste" | "chapitres" }) {
  const { childId = "" } = useParams();
  const prenom = useEnfants().find((enfant) => enfant.child_id === childId)?.display_name;

  const [etat, setEtat] = useState<EtatDuProgramme | null>(null);
  const [matiere, setMatiere] = useState<string>("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);

  const [fil, setFil] = useState<Tour[]>([]);
  const [propositions, setPropositions] = useState<PropositionDeVu[]>([]);
  const [question, setQuestion] = useState("");
  const [attente, setAttente] = useState(false);
  const [panne, setPanne] = useState<string | null>(null);
  const historique = useRef<Tour[]>([]);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const charge = await call("vuEnClasseEtat", { params: { child_id: childId } });
        if (!annule) setEtat(charge);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId]);

  const matieres: MatiereDuProgrammeOut[] = useMemo(() => etat?.matieres ?? [], [etat]);
  const courante = useMemo(
    () => matieres.find((une) => une.matiere === matiere) ?? matieres[0],
    [matieres, matiere],
  );

  /** Cocher, décocher : un seul verbe, et la réponse **remplace** l'état.
   *
   * L'écran affiche alors ce qui est stocké et non ce qu'il croit avoir envoyé,
   * ce qui est la seule façon qu'une case cochée ne mente jamais. */
  const basculer = async (competence: EtatDeCompetenceOut, vu: boolean) => {
    setEnvoi(competence.competency_id);
    setErreur(null);
    try {
      const apres = await call("vuEnClasseDeclarer", {
        params: { child_id: childId },
        body: { competency_ids: [competence.competency_id], vu },
      });
      setEtat(apres);
      if (vu) {
        setPropositions((restantes) =>
          restantes.filter((une) => une.competency_id !== competence.competency_id),
        );
      }
    } catch (echec) {
      setErreur(
        echec instanceof ApiError ? echec.message : "La déclaration n'est pas partie.",
      );
    } finally {
      setEnvoi(null);
    }
  };

  const demander = async () => {
    const saisie = question.trim();
    if (!saisie || attente || !courante) return;
    setQuestion("");
    setPanne(null);
    setAttente(true);
    const avant = historique.current;
    setFil((precedent) => [...precedent, { role: "parent", texte: saisie }]);
    try {
      const conseil = await call("vuEnClasseConseil", {
        params: { child_id: childId },
        body: { matiere: courante.matiere, question: saisie, historique: avant },
      });
      historique.current = [
        ...avant,
        { role: "parent", texte: saisie },
        { role: "tuteur", texte: conseil.reponse ?? "" },
      ];
      setFil((precedent) => [...precedent, { role: "tuteur", texte: conseil.reponse ?? "" }]);
      setPropositions(conseil.propositions ?? []);
    } catch (echec) {
      setPanne(
        echec instanceof ApiError
          ? echec.message
          : "Le tuteur n'a pas répondu. Vos cases sont intactes.",
      );
    } finally {
      setAttente(false);
    }
  };

  if (erreur && !etat) return <ErrorNote>{erreur}</ErrorNote>;
  if (!etat) return <Loading />;

  return (
    <Screen
      title="Ce qui a été vu"
      lead={
        prenom
          ? `Cochez ce que la classe de ${prenom} a traité : les chapitres cochés entrent dans son travail du soir.`
          : "Cochez ce que la classe a traité."
      }
      actions={
        <Link className="btn btn--quiet" to={`/parents/enfant/${childId}`}>
          Retour
        </Link>
      }
    >
      <p className="notice">
        Une case cochée vaut preuve : le chapitre entre dans le travail du soir, et le
        « on n'a pas encore vu ça en classe » que {prenom ?? "votre enfant"} aurait posé
        dessus est levé. Décochez-la si vous vous êtes trompé, rien n'est définitif.
      </p>

      {matieres.length === 0 ? (
        <Empty>Aucun programme n'est chargé pour cet enfant.</Empty>
      ) : (
        <>
          <div className="filtres">
            <label className="filtre">
              <span className="filtre__label">Matière</span>
              <select
                className="select"
                value={courante?.matiere ?? ""}
                onChange={(evenement) => {
                  setMatiere(evenement.target.value);
                  setPropositions([]);
                  setFil([]);
                  historique.current = [];
                }}
              >
                {matieres.map((une) => (
                  <option key={une.matiere} value={une.matiere}>
                    {une.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {erreur ? <p className="notice notice--attention">{erreur}</p> : null}

          {vue === "liste" ? (
            <Card title="Demander au tuteur">
              <p className="muted">
                Il ne coche rien : il propose, vous validez. Il lit le programme et ce que les
                professeurs ont écrit dans le cahier de textes — jamais le journal du soir de{" "}
                {prenom ?? "votre enfant"}.
              </p>

              <div className="chat chat--parent">
                {fil.map((tour, rang) => (
                  <article
                    key={rang}
                    className={`bubble bubble--${tour.role === "parent" ? "child" : "tutor"}`}
                  >
                    <p>{tour.texte}</p>
                  </article>
                ))}
                {attente ? <p className="muted">Le tuteur cherche…</p> : null}
              </div>

              {propositions.length > 0 ? (
                <>
                  <h3 className="card__title">Ce qu'il propose de considérer comme vu</h3>
                  {propositions.map((proposition) => (
                    <label className="check" key={proposition.competency_id}>
                      <input
                        type="checkbox"
                        checked={false}
                        disabled={envoi === proposition.competency_id}
                        onChange={() =>
                          void basculer(
                            {
                              competency_id: proposition.competency_id,
                              label: proposition.label,
                            },
                            true,
                          )
                        }
                      />
                      <span>
                        {proposition.label}
                        <br />
                        <span className="muted">{proposition.raison}</span>
                      </span>
                    </label>
                  ))}
                </>
              ) : null}

              {panne ? <p className="notice notice--attention">{panne}</p> : null}

              <form
                className="composer"
                onSubmit={(evenement) => {
                  evenement.preventDefault();
                  void demander();
                }}
              >
                <label className="sr-only" htmlFor="vu-question">
                  Votre question
                </label>
                <textarea
                  id="vu-question"
                  className="composer__input"
                  rows={2}
                  value={question}
                  onChange={(evenement) => setQuestion(evenement.target.value)}
                  placeholder="Le prof a dit qu'ils avaient fini Byzance, ça correspond à quoi ?"
                />
                <Button type="submit" disabled={!question.trim() || attente}>
                  Demander
                </Button>
              </form>
            </Card>
          ) : null}

          {vue === "chapitres" ? (
            <ParChapitre courante={courante} envoi={envoi} onBascule={basculer} />
          ) : (
            <Card title={courante ? courante.label : "Programme"}>
              {(courante?.competences ?? []).map((competence) => (
                <LigneDeCompetence
                  key={competence.competency_id}
                  competence={competence}
                  envoi={envoi}
                  onBascule={(une, vu) => void basculer(une, vu)}
                />
              ))}
            </Card>
          )}

        </>
      )}
    </Screen>
  );
}

/** Les compétences de la matière, sous leur chapitre, dans l'ordre que le
 *  serveur donne : les chapitres des ouvrages d'abord, les thèmes ensuite. */
function ParChapitre({
  courante,
  envoi,
  onBascule,
}: {
  courante: MatiereDuProgrammeOut | undefined;
  envoi: string | null;
  onBascule: (competence: EtatDeCompetenceOut, vu: boolean) => Promise<void>;
}) {
  const parId = new Map((courante?.competences ?? []).map((une) => [une.competency_id, une]));
  const chapitres = courante?.chapitres ?? [];
  if (chapitres.length === 0) return <Empty>Aucun chapitre pour cette matière.</Empty>;
  return (
    <>
      {chapitres.map((chapitre, rang) => (
        <Card key={`${rang}-${chapitre.titre}`} title={chapitre.titre}>
          <p className="muted">{origineDe(chapitre)}</p>
          {(chapitre.competency_ids ?? []).map((identifiant) => {
            const competence = parId.get(identifiant);
            return competence ? (
              <LigneDeCompetence
                key={identifiant}
                competence={competence}
                envoi={envoi}
                onBascule={(une, vu) => void onBascule(une, vu)}
              />
            ) : null;
          })}
        </Card>
      ))}
    </>
  );
}
